import {
  Box,
  Button,
  Card,
  Field,
  Flex,
  Heading,
  HStack,
  InfoBox,
  Input,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  notify,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useState } from "react";
import { MdArrowBack } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import {
  CreateCustomerCompanyRequest,
  customerCompanyService,
} from "services/customerCompanyService";
import { getThemeColor } from "theme/colors";

const NewCustomerCompany = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState<CreateCustomerCompanyRequest>({
    name: "",
    externalRef: "",
    allowSelfRegistration: false,
    defaultCredentialType: null,
    agentInstruction: "",
    isActive: true,
  });

  const handleChange = <K extends keyof CreateCustomerCompanyRequest>(
    key: K,
    value: CreateCustomerCompanyRequest[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setError(
        t("app.customerCompanies.nameRequired", {
          defaultValue: "Name is required.",
        }),
      );
      return;
    }
    setError("");
    setSaving(true);
    try {
      const payload: CreateCustomerCompanyRequest = {
        ...form,
        name: form.name.trim(),
        externalRef: form.externalRef?.trim() || null,
        agentInstruction: form.agentInstruction?.trim() || null,
      };
      const created = await customerCompanyService.create(payload);
      notify({
        type: "success",
        title: t("app.customerCompanies.created", {
          defaultValue: "Customer company created.",
        }),
      });
      navigate(`/admin/customer-companies/${created.id}`);
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          t("app.errors.requestFailed", { defaultValue: "Request failed" }),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack gap={4} w="full" maxW="900px">
      <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
        <HStack>
          <Button
            intent="ghost"
            onClick={() => navigate("/admin/customer-companies")}
          >
            <MdArrowBack /> {t("app.common.back", { defaultValue: "Back" })}
          </Button>
          <Heading>
            {t("app.customerCompanies.new.title", {
              defaultValue: "New Company",
            })}
          </Heading>
        </HStack>
      </Flex>

      {error && (
        <InfoBox variant="error">
          <Text color="red.700">{error}</Text>
        </InfoBox>
      )}

      <Card.Root
        borderWidth="1px"
        borderColor={getThemeColor("card.border")}
        borderRadius="md"
      >
        <Card.Body>
          <Stack gap={4}>
            <Field
              label={t("app.customerCompanies.fields.name", {
                defaultValue: "Name",
              })}
              required
              helperText={t("app.customerCompanies.fields.nameHelp", {
                defaultValue:
                  "Display name. Must be unique within this tenant (case-insensitive).",
              })}
            >
              <Input
                value={form.name}
                onChange={(e) => handleChange("name", e.target.value)}
                maxLength={255}
              />
            </Field>

            <Field
              label={t("app.customerCompanies.fields.externalRef", {
                defaultValue: "Company ID (optional)",
              })}
              helperText={t("app.customerCompanies.fields.externalRefHelp", {
                defaultValue:
                  "Stable external identifier. Auto-populated from the widget's userCompanyId on first chat; can be edited.",
              })}
            >
              <Input
                value={form.externalRef ?? ""}
                onChange={(e) => handleChange("externalRef", e.target.value)}
                maxLength={255}
              />
            </Field>

            <Box>
              <HStack justify="space-between">
                <Stack gap={1}>
                  <Text fontWeight="medium">
                    {t("app.customerCompanies.fields.allowSelfRegistration", {
                      defaultValue: "Allow self-registration",
                    })}
                  </Text>
                  <Text fontSize="sm" color="var(--text-secondary-color)">
                    {t(
                      "app.customerCompanies.fields.allowSelfRegistrationHelp",
                      {
                        defaultValue:
                          "When enabled, anyone with the registration URL can register against this company.",
                      },
                    )}
                  </Text>
                </Stack>
                <Switch
                  checked={form.allowSelfRegistration ?? false}
                  onCheckedChange={(e: any) =>
                    handleChange("allowSelfRegistration", e.checked)
                  }
                />
              </HStack>
            </Box>

            <Select
              label={t("app.customerCompanies.fields.defaultCredentialType", {
                defaultValue: "Default credential type (hint)",
              })}
              helperText={t(
                "app.customerCompanies.fields.defaultCredentialTypeHelp",
                {
                  defaultValue:
                    "Suggested default for the credential setup popup. Each customer can still choose their own.",
                },
              )}
              value={
                form.defaultCredentialType === null ||
                form.defaultCredentialType === undefined
                  ? [""]
                  : [String(form.defaultCredentialType)]
              }
              onValueChange={({ value }) => {
                const v = value[0] ?? "";
                handleChange(
                  "defaultCredentialType",
                  v === "" ? null : Number(v),
                );
              }}
              items={[
                {
                  value: "",
                  label: t("app.customerCompanies.credentialNoHint", {
                    defaultValue: "No hint (let customer choose)",
                  }),
                },
                {
                  value: "1",
                  label: t("app.customerCompanies.credentialPin", {
                    defaultValue: "PIN (4-6 digits)",
                  }),
                },
                {
                  value: "2",
                  label: t("app.customerCompanies.credentialPassword", {
                    defaultValue: "Password",
                  }),
                },
              ]}
            />

            <Field
              label={t("app.customerCompanies.fields.agentInstruction", {
                defaultValue: "Agent instruction (optional)",
              })}
              helperText={t(
                "app.customerCompanies.fields.agentInstructionHelp",
                {
                  defaultValue:
                    "Free-text guidance merged into the agent prompt at runtime, alongside the tenant-global instructions. Use for tone, branding, behavior hints.",
                },
              )}
            >
              <Textarea
                value={form.agentInstruction ?? ""}
                onChange={(e) =>
                  handleChange("agentInstruction", e.target.value)
                }
                rows={5}
              />
            </Field>

            <Box>
              <HStack justify="space-between">
                <Stack gap={1}>
                  <Text fontWeight="medium">
                    {t("app.customerCompanies.fields.isActive", {
                      defaultValue: "Active",
                    })}
                  </Text>
                  <Text fontSize="sm" color="var(--text-secondary-color)">
                    {t("app.customerCompanies.fields.isActiveHelp", {
                      defaultValue:
                        "When off, customers of this company are blocked from authenticating.",
                    })}
                  </Text>
                </Stack>
                <Switch
                  checked={form.isActive ?? true}
                  onCheckedChange={(e: any) =>
                    handleChange("isActive", e.checked)
                  }
                />
              </HStack>
            </Box>
          </Stack>
        </Card.Body>
      </Card.Root>

      <HStack justify="flex-end" gap={3}>
        <Button
          intent="secondary"
          onClick={() => navigate("/admin/customer-companies")}
          disabled={saving}
        >
          {t("app.common.cancel")}
        </Button>
        <Button intent="primary" onClick={handleSubmit} disabled={saving}>
          {saving
            ? t("app.common.saving", { defaultValue: "Saving..." })
            : t("app.common.save")}
        </Button>
      </HStack>
    </Stack>
  );
};

export default NewCustomerCompany;
